import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import queryString from "query-string";
import { useQueryClient } from "@tanstack/react-query";

import {
  QUERY_WIDTHS,
  TABLE_TRANSACTIONS_COLUMNS,
  PER_PAGE_VALUES,
} from "@utils/index";
import {
  TransactionModel,
  WidgetsTotalModel,
  AssetModel,
  WidgetsCategoryModel,
  WidgetsMileageModel,
} from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  useTransactionGetAll,
  useTransactionGetOne,
  useTransactionDelete,
  useTransactionGetWidgets,
  useUserGetPeriods,
  useAssetGetBusinessAssets,
  useSubmissionGetAll,
} from "@providers/index";
import { useBusinessType, useNotify } from "@hooks/index";
import {
  PageContentContainer,
  PageContentTitle,
  Table,
  TableHead,
  TableBody,
  TrNoItems,
  TableRowTransaction,
  Pagination,
  PerPage,
  RangePicker,
  ConfirmModal,
  BusinessSelector,
  AssetSelector,
  TotalsWidgetCard,
  WidgetItemList,
  DesktopPageTitle,
  UploadLaterBannerDashboard,
  TutorialButton,
} from "@components/index";
import {
  TransactionDetails,
  WidgetCategoryDetails,
  WidgetMileageDetails,
} from "@pages/index";

export const DashboardPage = () => {
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const location = useLocation();
  const navigate = useNavigate();
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const queryClient = useQueryClient();

  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const [businessId, setBusinessId] = useState("");
  const businessType = useBusinessType(businessId);
  const [submissionId, setSubmissionId] = useState("");

  const [assets, setAssets] = useState<AssetModel[]>([]);
  const [assetId, setAssetId] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [startCurrentPeriod, setStartCurrentPeriod] = useState("");
  const [endCurrentPeriod, setEndCurrentPeriod] = useState("");

  const [widgetsTotal, setWidgetsTotal] = useState<WidgetsTotalModel>();
  const [widgetsItems, setWidgetsItems] = useState<WidgetsCategoryModel[]>([]);

  const [transactions, setTransactions] = useState<TransactionModel[]>();

  const [selectedPage, setSelectedPage] = useState(1);
  const [countTransactions, setCountTransactions] = useState(0);
  const [perPage, setPerPage] = useState(PER_PAGE_VALUES[0]);

  const [redirectNeeds, setRedirectNeeds] = useState(false);

  const [transactionId, setTransactionId] = useState("");
  const [transactionDetails, setTransactionDetails] =
    useState<TransactionModel>();
  const [removeId, setRemoveId] = useState("");

  const [categoryWidgetDetails, setCategoryWidgetDetails] =
    useState<WidgetsCategoryModel>();
  const [mileageWidgetDetails, setMileageWidgetDetails] =
    useState<WidgetsMileageModel>();

  useEffect(() => {
    const params = queryString.parse(location.search);
    setBusinessId(
      (params?.businessId as string) ||
        defaultBusiness?.uniqueId ||
        businesses[0]?.uniqueId ||
        "",
    );
    setAssetId((params?.assetId as string) || "");
    setSelectedPage(parseInt((params?.page || "1").toString()));
    setPerPage((params?.perPage as string) || PER_PAGE_VALUES[0]);
    setStartDate(params?.startDate as string);
    setEndDate(params?.endDate as string);
  }, [location, defaultBusiness, businesses]);

  const widgetsTitle = useMemo(() => {
    const businessTitle = businesses.find(
      ({ uniqueId }) => uniqueId === businessId,
    )?.name;
    return businessTitle || defaultBusiness?.name || businesses[0]?.name;
  }, [businessId, businesses, defaultBusiness]);

  const availableTutorialNames = useMemo(() => {
    const arr = [];
    if (
      transactions?.filter(({ status }) =>
        ["open", "reopened"].includes(status.value),
      )?.length
    ) {
      arr.push("dashboardViewTutorial");
    }
    return arr;
  }, [transactions]);

  const {
    isPending: isPendingGetAssets,
    isSuccess: isSuccessGetAssets,
    data: dataGetAssets,
    isError: isErrorGetAssets,
    error: errorGetAssets,
  } = useAssetGetBusinessAssets(businessId);

  useEffect(() => {
    if (isSuccessGetAssets && dataGetAssets) {
      setAssets(dataGetAssets);
    }
  }, [isSuccessGetAssets, dataGetAssets]);

  useEffect(() => {
    if (isErrorGetAssets && errorGetAssets) {
      notify("error", errorGetAssets.message);
    }
  }, [isErrorGetAssets, errorGetAssets, notify]);

  const {
    isPending: isPendingGetTransactions,
    isSuccess: isSuccessGetTransactions,
    data: dataGetTransactions,
    isError: isErrorGetTransactions,
    error: errorGetTransactions,
  } = useTransactionGetAll({
    businessId,
    submissionId,
    assetId,
    startDate,
    endDate,
    page: selectedPage,
    perPage,
  });

  useEffect(() => {
    if (isSuccessGetTransactions && dataGetTransactions) {
      setTransactions(dataGetTransactions.transactions);
      setCountTransactions(dataGetTransactions.pagination.total);
    }
  }, [isSuccessGetTransactions, dataGetTransactions]);

  useEffect(() => {
    if (isErrorGetTransactions && errorGetTransactions) {
      notify("error", errorGetTransactions.message);
    }
  }, [isErrorGetTransactions, errorGetTransactions, notify]);

  const {
    isPending: isPendingGetPeriods,
    isSuccess: isSuccessGetPeriods,
    data: dataGetPeriods,
    isError: isErrorGetPeriods,
    error: errorGetPeriods,
  } = useUserGetPeriods({ enabled: true });

  useEffect(() => {
    if (isSuccessGetPeriods && dataGetPeriods?.currentPeriod) {
      setStartDate(dataGetPeriods.currentPeriod.startDate.split("T")[0]);
      setEndDate(dataGetPeriods.currentPeriod.endDate.split("T")[0]);
      setStartCurrentPeriod(
        dataGetPeriods.currentPeriod.startDate.split("T")[0],
      );
      setEndCurrentPeriod(dataGetPeriods.currentPeriod.endDate.split("T")[0]);
      setRedirectNeeds(true);
    }
  }, [isSuccessGetPeriods, dataGetPeriods]);

  useEffect(() => {
    if (isErrorGetPeriods && errorGetPeriods) {
      notify("error", errorGetPeriods.message);
    }
  }, [isErrorGetPeriods, errorGetPeriods, notify]);

  const { data: dataGetSubmissions } = useSubmissionGetAll({ businessId });

  useEffect(() => {
    if (!startDate || !endDate || !dataGetSubmissions?.submissions.length) {
      return;
    }
    const match = dataGetSubmissions.submissions.find(
      ({ period }) =>
        period.startDate.split("T")[0] === startDate &&
        period.endDate.split("T")[0] === endDate,
    );
    setSubmissionId(match?.uniqueId ?? "");
  }, [startDate, endDate, dataGetSubmissions]);

  const {
    isPending: isPendingGetWidgets,
    isSuccess: isSuccessGetWidgets,
    isStale: isStaleGetWidgets,
    data: dataGetWidgets,
    isError: isErrorGetWidgets,
    error: errorGetWidgets,
  } = useTransactionGetWidgets({
    businessId,
    startDate,
    endDate,
    assetId,
  });

  useEffect(() => {
    if (isSuccessGetWidgets && dataGetWidgets) {
      setWidgetsTotal(dataGetWidgets.totals);
      setWidgetsItems(dataGetWidgets.categories);
    }
  }, [isSuccessGetWidgets, dataGetWidgets]);

  useEffect(() => {
    if (isErrorGetWidgets && errorGetWidgets) {
      notify("error", errorGetWidgets.message);
    }
  }, [isErrorGetWidgets, errorGetWidgets, notify]);

  const {
    isPending: isPendingGetOneTransaction,
    isSuccess: isSuccessGetOneTransaction,
    isStale: isStaleGetOneTransaction,
    data: dataGetOneTransaction,
    isError: isErrorGetOneTransaction,
    error: errorGetOneTransaction,
  } = useTransactionGetOne(transactionId);

  useEffect(() => {
    if (isErrorGetOneTransaction && errorGetOneTransaction) {
      notify("error", errorGetOneTransaction.message);
    }
  }, [isErrorGetOneTransaction, errorGetOneTransaction, notify]);

  useEffect(() => {
    if (isSuccessGetOneTransaction && dataGetOneTransaction) {
      setTransactionDetails(dataGetOneTransaction);
    }
  }, [isSuccessGetOneTransaction, dataGetOneTransaction]);

  const {
    mutate: mutateDeleteTransaction,
    isPending: isPendingDeleteTransaction,
  } = useTransactionDelete(
    () => {
      notify("success", "Your Transaction has been successfully deleted.");
      handleCloseTransactionDetails();
      setRemoveId("");
      queryClient.invalidateQueries({ queryKey: ["transactionGetAll"] });
      queryClient.invalidateQueries({ queryKey: ["transactionGetWidgets"] });
    },
    ({ message }) => notify("error", message),
  );

  useEffect(() => {
    setState({
      isLoading:
        isPendingGetAssets ||
        isPendingGetTransactions ||
        isPendingGetPeriods ||
        (isStaleGetWidgets && isPendingGetWidgets) ||
        (isStaleGetOneTransaction && isPendingGetOneTransaction) ||
        isPendingDeleteTransaction,
    });
  }, [
    isPendingGetAssets,
    isPendingGetTransactions,
    isPendingGetPeriods,
    isStaleGetWidgets,
    isPendingGetWidgets,
    isStaleGetOneTransaction,
    isPendingGetOneTransaction,
    isPendingDeleteTransaction,
    setState,
  ]);

  useEffect(() => {
    if (!redirectNeeds) {
      return;
    }
    setRedirectNeeds(false);
    const params = queryString.stringify(
      {
        businessId,
        assetId,
        page: selectedPage,
        perPage,
        startDate,
        endDate,
      },
      { skipEmptyString: true },
    );
    navigate(`?${params}`);
  }, [
    redirectNeeds,
    navigate,
    businessId,
    assetId,
    selectedPage,
    perPage,
    startDate,
    endDate,
  ]);

  const handleSelectBusiness = (businessId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setBusinessId(businessId);
    setSubmissionId("");
    setAssetId("");
  };

  const handleSelectAsset = (assetId: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setAssetId(assetId);
  };

  const handleSetStartDate = (startDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setStartDate(startDate);
  };

  const handleSetEndDate = (endDate: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setEndDate(endDate);
  };

  const handleSelectPage = (page: number) => {
    setRedirectNeeds(true);
    setSelectedPage(page);
  };

  const handleCloseTransactionDetails = () => {
    setTransactionId("");
    setTransactionDetails(undefined);
  };

  const handleSelectPerPage = (value: string) => {
    setRedirectNeeds(true);
    setSelectedPage(1);
    setPerPage(value);
  };

  const handleCloseCategoryWidgetDetails = () =>
    setCategoryWidgetDetails(undefined);
  const handleCloseMileageWidgetDetails = () =>
    setMileageWidgetDetails(undefined);

  return (
    <div className="md:pt-6">
      {!isMobile && (
        <DesktopPageTitle
          title="Dashboard"
          tutorialNames={availableTutorialNames}
          faqCategory={["Dashboard"]}
        />
      )}
      <div className="flex flex-col justify-between gap-y-4 px-4 pt-4 md:flex-row md:px-10 md:pt-6">
        <BusinessSelector
          height="small"
          width="w-full md:w-64"
          value={businessId}
          onSetBusiness={handleSelectBusiness}
          dataTestid="filter_businessSelector"
        />
        <RangePicker
          startDate={startDate}
          endDate={endDate}
          prefix="dashboardPage"
          onSetStartDate={handleSetStartDate}
          onSetEndDate={handleSetEndDate}
          showPeriods={true}
          disableClear
          dataTestid="filter_dateRangeSelector"
        />
      </div>
      <PageContentContainer>
        <div className="flex flex-col justify-between pb-2 md:flex-row md:pb-0">
          <PageContentTitle title={widgetsTitle} />
          {isMobile && (
            <TutorialButton
              variant="dark"
              tutorialNames={availableTutorialNames}
              faqCategory={["Dashboard"]}
            />
          )}
          {!disabledFeatures.includes("Assets") && (
            <div className="mt-2 w-full md:mt-0 md:w-[300px]">
              <AssetSelector
                items={assets}
                height="small"
                hideAddAssetItem
                hideUploadLaterInfo
                value={assetId}
                onSetAsset={handleSelectAsset}
                dataTestid="filter_assetSelector"
              />
            </div>
          )}
        </div>
        <UploadLaterBannerDashboard
          assets={assets}
          startDate={startCurrentPeriod}
          endDate={endCurrentPeriod}
        />
        <div className="mt-4 flex flex-col items-stretch gap-x-10 gap-y-4 md:mt-6 md:flex-row">
          <div
            className="relative flex items-stretch"
            data-testid="dashboard_totalWidget"
          >
            <TotalsWidgetCard data={widgetsTotal} businessType={businessType} />
            <div className="absolute -bottom-2 -right-6 -top-2 hidden w-1.5 justify-center overflow-hidden opacity-25 md:flex">
              <div className="h-full w-px bg-secondary-100" />
              <div className="absolute -top-[3px] h-1.5 w-1.5 rotate-45 bg-secondary-100" />
              <div className="absolute -bottom-[3px] h-1.5 w-1.5 rotate-45 bg-secondary-100" />
            </div>
          </div>
          <WidgetItemList
            categories={widgetsItems}
            mileage={dataGetWidgets?.mileage}
            businessId={businessId}
            startDate={startDate}
            endDate={endDate}
            onCategoryClick={setCategoryWidgetDetails}
            onMileageClick={setMileageWidgetDetails}
          />
        </div>
        <div className="mt-4 px-2 py-2 text-300 font-bold text-black-100 md:mt-10">
          Recent Transactions
        </div>
        <Table
          className="px-4 pb-4 md:px-2 md:pb-0"
          dataTestid="table_dashboardTable"
        >
          {!isMobile && (
            <TableHead
              columns={TABLE_TRANSACTIONS_COLUMNS.filter(
                ({ title }) =>
                  title !== "Asset" || !disabledFeatures.includes("Assets"),
              )}
              dataTestid="thead_dashboardTable"
            />
          )}
          <TableBody dataTestid="tbody_dashboardTable">
            {transactions?.length ? (
              transactions.map((transaction) => (
                <TableRowTransaction
                  key={transaction.uniqueId}
                  transaction={transaction}
                  hideAssetColumn={disabledFeatures.includes("Assets")}
                  onDetails={() => setTransactionId(transaction.uniqueId)}
                  onRemove={() => setRemoveId(transaction.uniqueId)}
                />
              ))
            ) : (
              <TrNoItems title="No transactions found" />
            )}
          </TableBody>
        </Table>
        {countTransactions > 0 && (
          <div className="flex flex-col gap-y-6 md:flex-row md:gap-x-6">
            <Pagination
              count={countTransactions}
              perPage={parseInt(perPage, 10)}
              currentPage={selectedPage}
              onSelect={handleSelectPage}
            />
            <PerPage perPage={perPage} onSelect={handleSelectPerPage} />
          </div>
        )}
      </PageContentContainer>
      <TransactionDetails
        transaction={transactionDetails}
        onRemove={() => mutateDeleteTransaction(transactionDetails!.uniqueId)}
        onClose={handleCloseTransactionDetails}
      />
      {categoryWidgetDetails && (
        <WidgetCategoryDetails
          widget={categoryWidgetDetails}
          businessId={businessId}
          startDate={startDate}
          endDate={endDate}
          onClose={handleCloseCategoryWidgetDetails}
        />
      )}
      {mileageWidgetDetails && (
        <WidgetMileageDetails
          mileage={mileageWidgetDetails}
          businessId={businessId}
          startDate={startDate}
          endDate={endDate}
          onClose={handleCloseMileageWidgetDetails}
        />
      )}
      <ConfirmModal
        show={!!removeId}
        confirmButtonTitle="Delete"
        text1="Are you sure?"
        text2="Do you really want to delete this transaction?"
        onClickCancel={() => setRemoveId("")}
        onClickConfirm={() => mutateDeleteTransaction(removeId)}
      />
    </div>
  );
};
